import { AlertFactory, AlertState } from 'alerts/Alert'
import { InsightStore } from 'jobs/insights/InsightStore'
import Job from 'jobs/Job'
import JobStore from 'jobs/JobStore'
import ConversationStore from 'messaging/ConversationStore'
import { makeObservable, computed } from 'mobx'
import { task } from 'mobx-task'
import { concat, Subscription } from 'rxjs'
import { concatMap, filter } from 'rxjs/operators'

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  year: '2-digit',
  month: '2-digit',
  day: '2-digit',
})

const timeFormatter = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: 'numeric',
  hour12: true,
})

function formatDateTime(date: Date) {
  return `${dateFormatter.format(date)} at ${timeFormatter.format(date)}`
}

export class JobInsightsViewModel {
  /**
   * Manages an Rx subscription while the VM is active
   */
  private subscription = new Subscription()

  constructor(
    private readonly job: Job,
    private readonly jobStore: JobStore,
    private readonly jobInsightStore: InsightStore,
    private readonly conversationStore: ConversationStore,
    private newAlert: AlertFactory,
  ) {
    makeObservable(this, {
      latestInsights: computed,
      isLatestInsightsAvailable: computed,
      isMoreThanZeroMessagesAvailable: computed,
      latestInsightGeneratedDate: computed,
      lastGeneratedTimeString: computed,
      isAnyIrrelevant: computed,
      insightsLabelText: computed,
      isRegenerateNext: computed,
    })
  }

  get isMoreThanZeroMessagesAvailable() {
    const conversation = this.conversationStore.conversations.find(
      (c) => c.id === this.job.conversation?.id,
    )
    if (conversation === undefined) {
      return false
    }

    return conversation.humanMessagesCount > 0
  }

  get latestInsights() {
    return this.jobInsightStore.latestInsightsForJob(this.job.id!)
  }

  get isLatestInsightsAvailable() {
    return this.latestInsights.length > 0
  }

  get latestInsightGeneratedDate() {
    return this.latestInsights[0]?.dateGenerated ?? null
  }

  get isAnyIrrelevant() {
    return (
      this.jobInsightStore
        .latestInsightsForJob(this.job.id!)
        .some((i) => i.isIrrelevant) ?? false
    )
  }

  get isRegenerateNext() {
    return this.isLatestInsightsAvailable && this.isAnyIrrelevant
  }

  get lastGeneratedTimeString() {
    if (this.latestInsightGeneratedDate === null) {
      return null
    }

    const date = new Date(this.latestInsightGeneratedDate)
    return formatDateTime(date)
  }

  get insightsLabelText() {
    if (this.regenerateOrGenerateMore.pending && this.isRegenerateNext) {
      return 'Regenerating'
    }

    if (this.regenerateOrGenerateMore.pending) {
      return 'Generating'
    }

    if (this.isRegenerateNext) {
      return 'Regenerate'
    }

    if (!this.isAnyIrrelevant && this.latestInsights.length < 5) {
      return 'Generate More'
    }

    return null
  }

  regenerateOrGenerateMore = task.resolved(async () => {
    try {
      await this.jobInsightStore.generateOrRefreshJobInsights(this.job.id)
    } catch (error) {
      this.newAlert()
        .withState(AlertState.Error)
        .saying('Insights Error', error.message)
        .autoDismiss()
        .show()
      console.error('Failed to generate insights for job', this.job.id, error)
    }
  })

  // We seperate this to avoid calling fetch job query insights twice since we may call this at page load.
  // Errors are also not shown unless the user clicks the 'Generate More' button.
  regenerateInsightsOnPageLoad = task.resolved(async () => {
    try {
      await this.jobInsightStore.generateOrRefreshJobInsights(this.job.id)
    } catch (error) {
      console.error('Failed to generate insights for job', this.job.id, error)
    }
  })

  /**
   * Activate the view model.
   */
  activate = task.resolved(async () => {
    if (this.job.id === undefined) {
      return null
    }
    try {
      await this.conversationStore.fetchHumanMessagesTotalCount(
        this.job.conversation?.id,
        this.job.workspace.id,
      )
      await this.jobInsightStore.fetchLatestJobInsights(this.job.id)

      // Automatically regenerate insights on page load, if any are irrelevant.
      if (this.isRegenerateNext) {
        await this.regenerateInsightsOnPageLoad()
      }

      this.subscription = new Subscription()
      this.subscription.add(
        this.jobStore.jobUpdatedEvents$
          .pipe(
            filter((e) => e.job.id === this.job.id),
            filter(
              () => this.isLatestInsightsAvailable && !this.isAnyIrrelevant,
            ),
            concatMap(() =>
              concat(this.jobInsightStore.fetchLatestJobInsights(this.job.id)),
            ),
          )
          .subscribe(),
      )
    } catch (error) {
      console.error('Failed to load insights for job', this.job.id, error)
    }
  })

  /**
   * Deactivate the view model.
   */
  deactivate() {
    this.activate.reset()
    this.subscription.unsubscribe()
  }
}
